import { ReactNode, useCallback, useState } from 'react'
import { useRouter } from 'next/router'
import type { AccessDenied } from 'shared/errors/access-denied'
import { AccessDeniedReasonsEnum } from 'shared/errors/access-denied'
import { useCourseQuiz } from 'modules/course/hooks/use-course-quiz'
import type {
  CourseQuizDataInProgressInterface,
  CourseQuizDataResultInterface,
  CourseQuizId,
} from 'modules/course/types/course-quiz-interface'
import { CourseContentSkeleton } from '../components/course-content-skeleton'
import { CourseNeedCompleteModuleScreen } from '../components/course-info-screens/course-need-complete-module-screen'
import CourseTracker from '../components/course-tracker'
import { useCourseData } from '../hooks/use-course-data'
import { getContentType } from '../hooks/use-step-router/utils'
import { isCourseQuizDataResult } from '../utils/is-course-quiz-data-result'
import { CourseQuizFailScreen } from './course-quiz-fail-screen'
import { CourseQuizInProgressScreen } from './course-quiz-in-progress-screen'
import { CourseQuizPassScreen } from './course-quiz-pass-screen'
import { CourseQuizResultsScreen } from './course-quiz-results-screen'
import { CourseQuizStateContext, CourseQuizStateContextProvider } from './course-quiz-state/context'
import type { QuizStateType } from './course-quiz-state/types'
import { CourseStatusEnum } from './course-quiz-state/types'

interface CourseQuizProps {
  quizId: CourseQuizId
}

const getQuizContent = (
  quizState: QuizStateType | null,
  courseQuizData: CourseQuizDataInProgressInterface | CourseQuizDataResultInterface,
): ReactNode => {
  switch (quizState?.status) {
    case CourseStatusEnum.failed: {
      return <CourseQuizFailScreen />
    }
    case CourseStatusEnum.pass: {
      return <CourseQuizPassScreen />
    }
    case CourseStatusEnum.in_progress: {
      return (
        <CourseQuizInProgressScreen
          courseQuizData={courseQuizData as CourseQuizDataInProgressInterface}
        />
      )
    }
    default: {
      return isCourseQuizDataResult(courseQuizData) ? (
        <CourseQuizResultsScreen courseQuizData={courseQuizData as CourseQuizDataResultInterface} />
      ) : (
        <CourseContentSkeleton />
      )
    }
  }
}

export const CourseQuiz = ({ quizId }: CourseQuizProps): ReactNode => {
  const { route } = useRouter()
  const contentType = getContentType(route)
  const [blockedByLecture, setBlockedByLecture] = useState(false)

  const onProgressLocked = useCallback((error: AccessDenied) => {
    const data = error.response?.data
    const reason =
      typeof data === 'object' && data !== null && 'reason' in data
        ? (data as { reason?: string }).reason
        : undefined

    const isLocked = reason === AccessDeniedReasonsEnum.BlockedByProgress

    if (isLocked) {
      setBlockedByLecture(true)
      return
    }

    throw error
  }, [])

  const { courseQuizData } = useCourseQuiz({
    courseQuizId: quizId,
    shouldFetch: contentType === 'quiz',
    accessDeniedResponseHandler: onProgressLocked,
  })

  const { courseData } = useCourseData()

  const trackingEnabled = !!courseData?.lectureSessionLogging && !!courseQuizData

  return blockedByLecture ? (
    <CourseNeedCompleteModuleScreen />
  ) : courseQuizData ? (
    <CourseQuizStateContextProvider quizId={quizId}>
      {trackingEnabled && <CourseTracker />}
      <CourseQuizStateContext.Consumer>
        {({ quizState }) => getQuizContent(quizState, courseQuizData)}
      </CourseQuizStateContext.Consumer>
    </CourseQuizStateContextProvider>
  ) : (
    <CourseContentSkeleton />
  )
}
